import { useState, useCallback, useMemo, useEffect } from "react";
import { LegendKey } from "../models/chart_model"; // adjust path to your project

/**
 * useLegendFilter
 *
 * Centralises the "click a legend on the chart → table/summary
 * instantly shows only those columns" pattern.
 *
 * Usage in ANY page that has a ReMixChart + ChartTable / LegendSummaryTable:
 *
 *   const allLegends = useMemo(() => getLegends(), [chartKeys, selectedUnit]);
 *   const { visibleLegends, handleLegendSelect, selectedLegends } =
 *         useLegendFilter(allLegends);
 *
 *   // on the chart
 *   <ReMixChart
 *     legends={allLegends}                          // chart always sees full list
 *     defaultSelectedLegends={selectedLegends}     // keeps chart in sync
 *     onLegendSelect={handleLegendSelect}          // writes back to hook
 *   />
 *
 *   // on table / summary  — just pass visibleLegends
 *   <ChartTable           legends={visibleLegends} ... />
 *   <LegendSummaryTable   legends={visibleLegends} ... />
 *   downloadChartTableCSV({ legends: visibleLegends, ... })
 *
 * @param allLegends   – the FULL legend array the chart needs (memoised in the page).
 *                       The hook watches this reference; when it changes (e.g. unit
 *                       switch, new data fetch) the selection resets to "all".
 */
export function useLegendFilter(
  allLegends: LegendKey[],
  resetDeps: any[] = [],
) {
  // ── internal state ──────────────────────────────────────────────────────
  // Empty array = "everything is visible" (mirrors ReMixChart's own convention).
  const [selectedLegends, setSelectedLegends] = useState<LegendKey[]>([]);

  // ── reset when the legend list itself changes ──────────────────────────
  // Fires when chartKeys change, unit changes, or new data arrives –
  // anything that rebuilds the legend array.  We watch allLegends reference
  // and any optional resetDeps (like chartData) to ensure a clean "Show All" state.
  useEffect(() => {
    setSelectedLegends([]); // back to "show all"
  }, [allLegends, ...resetDeps]);

  // ── callback the chart calls on every legend toggle ─────────────────────
  // ReMixChart already passes the NEW full-selection array; we just store it.
  const handleLegendSelect = useCallback((legends: LegendKey[]) => {
    setSelectedLegends(legends);
  }, []);

  // ── derive what the table / summary should see ─────────────────────────
  const visibleLegends = useMemo(() => {
    // "nothing selected" or "everything selected" → show all
    if (
      selectedLegends.length === 0 ||
      selectedLegends.length === allLegends.length
    ) {
      return allLegends;
    }
    // otherwise keep only the ones the user toggled ON, preserving original order
    return allLegends.filter((leg) =>
      selectedLegends.some((sel) => sel.dataKey === leg.dataKey),
    );
  }, [selectedLegends, allLegends]);

  // ── expose ──────────────────────────────────────────────────────────────
  return {
    /** pass to <ChartTable> / <LegendSummaryTable> / download functions */
    visibleLegends,
    /** pass to <ReMixChart onLegendSelect={…}> */
    handleLegendSelect,
    /** pass to <ReMixChart defaultSelectedLegends={…}> */
    selectedLegends,
  };
}
/**
 * AnyOtherPage.tsx  —  blueprint / recipe
 *
 * Copy these exact changes into every page that uses
 * ReMixChart + ChartTable / LegendSummaryTable.
 *
 * Total lines changed per page:  ~6
 * ─────────────────────────────────────────────────────
 */

// ══════════════════════════════════════════════════════
// STEP 1 — one new import at the top
// ══════════════════════════════════════════════════════
// import { useLegendFilter } from "../../hooks/useLegendFilter";   // adjust path

// ══════════════════════════════════════════════════════
// STEP 2 — inside the component, REPLACE the legend
//          builder with a memoised version, then call
//          the hook.  (3 lines)
// ══════════════════════════════════════════════════════

//  ❌  BEFORE (whatever your page had):
//      function getLegends() { … }
//      … legends: getLegends() …

//  ✅  AFTER:
// const allLegends = useMemo(() => {
//   // ↓ paste your existing getLegends() body here
//   return chartKeys.map((key) => ({
//     dataKey: key + "_" + selectedUnit.toLowerCase(),
//     name:    someNameFunction(key),
//     stroke:  someColorFunction(key),
//   }));
// }, [chartKeys, selectedUnit /*, …any other deps your getLegends used */]);

// const { visibleLegends, handleLegendSelect, selectedLegends } =
//   useLegendFilter(allLegends);

// ══════════════════════════════════════════════════════
// STEP 3 — wire the three values into JSX
// ══════════════════════════════════════════════════════

//  <ReMixChart
//    legends={allLegends}                          // FULL list — chart needs it
//    onLegendSelect={handleLegendSelect}           // hook's callback
//    defaultSelectedLegends={selectedLegends}      // ← NEW prop (keeps chart synced)
//    … everything else unchanged …
//  />
//
//  <ChartTable            legends={visibleLegends} … />
//  <LegendSummaryTable    legends={visibleLegends} … />
//  downloadChartTableCSV({ legends: visibleLegends, … })
//  downloadSummaryCSV    ({ legends: visibleLegends, … })

// ══════════════════════════════════════════════════════
// THAT'S IT.  Nothing to change in SummaryTable.tsx
// or ReMixCharts.tsx.  The hook + 3 wiring points is
// the entire diff for each page.
// ══════════════════════════════════════════════════════